import { useMemo } from "react";
import type { MeshStandardMaterialParameters } from "three";
import * as THREE from "three";
import type { PanelColor } from "../interfaces/PanelColor";

// Texture cache to avoid regenerating the same texture multiple times
const textureCache = new Map<string, THREE.CanvasTexture>();

// Simple 2D noise function for canvas-based texture generation
function noise(x: number, y: number): number {
  const n = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;
  return n - Math.floor(n);
}

// Simplified FBM with fewer octaves for better performance
function fbm(x: number, y: number, octaves: number): number {
  let value = 0;
  let amplitude = 0.5;
  let frequency = 1;
  // Reduced to 4 octaves for better performance
  const maxOctaves = Math.min(octaves, 4);
  for (let i = 0; i < maxOctaves; i++) {
    value += amplitude * noise(x * frequency, y * frequency);
    frequency *= 2;
    amplitude *= 0.5;
  }
  return value;
}

// Generate height map for normal map calculation (optimized)
function generateHeightMap(size: number, textureScale: number): Float32Array {
  const heightMap = new Float32Array(size * size);
  const step = Math.max(1, Math.floor(size / 128)); // Sample every Nth pixel for large textures

  for (let y = 0; y < size; y += step) {
    for (let x = 0; x < size; x += step) {
      const u = (x / size) * textureScale;
      const v = (y / size) * textureScale;

      // Simplified pattern with fewer octaves
      const grain = fbm(u, v, 3);
      const creases = fbm(u * 0.3, v * 0.3, 2) * 0.5;
      const pattern = (grain * 0.7 + creases * 0.3) * 0.5 + 0.5;

      // Fill the block if using step > 1
      for (let dy = 0; dy < step && y + dy < size; dy++) {
        for (let dx = 0; dx < step && x + dx < size; dx++) {
          heightMap[(y + dy) * size + (x + dx)] = pattern;
        }
      }
    }
  }

  return heightMap;
}

// Generate a normal map from height map (optimized with caching)
function generateNormalMap(
  size: number,
  textureScale: number,
  intensity: number,
): THREE.CanvasTexture {
  // Create cache key based on parameters
  const cacheKey = `${size}-${textureScale.toFixed(2)}-${intensity.toFixed(2)}`;
  const cached = textureCache.get(cacheKey);
  if (cached) {
    return cached.clone();
  }

  const heightMap = generateHeightMap(size, textureScale);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not get canvas context");
  }

  const imageData = ctx.createImageData(size, size);
  const data = imageData.data;

  // Simplified gradient calculation
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const getHeight = (px: number, py: number) => {
        const pxWrapped = ((px % size) + size) % size;
        const pyWrapped = ((py % size) + size) % size;
        return heightMap[pyWrapped * size + pxWrapped] ?? 0;
      };

      // Simple gradient calculation (faster than full Sobel)
      const hL = getHeight(x - 1, y);
      const hR = getHeight(x + 1, y);
      const hD = getHeight(x, y - 1);
      const hU = getHeight(x, y + 1);

      const dx = (hR - hL) * intensity;
      const dy = (hU - hD) * intensity;
      const dz = Math.sqrt(Math.max(0, 1 - dx * dx - dy * dy));

      const index = (y * size + x) * 4;
      data[index] = Math.floor((dx * 0.5 + 0.5) * 255);
      data[index + 1] = Math.floor((dy * 0.5 + 0.5) * 255);
      data[index + 2] = Math.floor((dz * 0.5 + 0.5) * 255);
      data[index + 3] = 255;
    }
  }

  ctx.putImageData(imageData, 0, 0);

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;

  // Cache the texture (limit cache size to prevent memory issues)
  if (textureCache.size > 10) {
    const firstKey = textureCache.keys().next().value;
    if (firstKey) {
      textureCache.delete(firstKey);
    }
  }
  textureCache.set(cacheKey, texture);

  return texture;
}

interface LeatherPanelMaterialProps {
  color: PanelColor;
  /** Panel size for texture scaling - ensures consistent texture scale regardless of cutouts */
  panelSize?: THREE.Vector2;
  /** Texture scale - higher values = finer grain (overrides panelSize-based calculation if provided) */
  textureScale?: number;
  /** Base roughness (0-1) */
  roughness?: number;
  /** Base metalness (0-1) */
  metalness?: number;
  /** Normal map intensity - higher = more visible texture */
  normalIntensity?: number;
  /** Texture resolution (power of 2 recommended) */
  textureSize?: number;
}

export const LeatherPanelMaterial = ({
  color,
  panelSize,
  textureScale: explicitTextureScale,
  roughness = 0.2,
  metalness: explicitMetalness,
  normalIntensity = 0.5,
  textureSize = 256,
}: LeatherPanelMaterialProps) => {
  const threeColor = useMemo(() => new THREE.Color(color.color), [color.color]);
  const metalness = explicitMetalness ?? (color.metalic ? 0.7 : 0.5);

  // Calculate texture scale based on panel size if not explicitly provided
  // This ensures consistent texture scale regardless of cutouts
  // Default: 1 texture unit per 10mm of panel size
  const textureScale = useMemo(() => {
    if (explicitTextureScale !== undefined) {
      return explicitTextureScale;
    }
    if (panelSize) {
      // Use average of width and height for consistent scaling
      const avgSize = (panelSize.x + panelSize.y) / 2;
      // Scale: 80 texture units per 100mm (default behavior)
      return (avgSize / 300) * 80;
    }
    // Fallback to default
    return 80.0;
  }, [explicitTextureScale, panelSize]);

  // Generate normal map for visible surface detail
  const normalMap = useMemo(() => {
    const map = generateNormalMap(textureSize, textureScale, normalIntensity);

    // Set texture repeat based on panel size to ensure consistent physical scale
    if (panelSize) {
      // Calculate repeat: texture should repeat based on actual panel dimensions
      // This compensates for UV stretching/distortion with cutouts
      const repeatX = panelSize.x / 300; // 300mm = 1 repeat unit
      const repeatY = panelSize.y / 300;
      map.repeat.set(repeatX, repeatY);
    }

    return map;
  }, [textureSize, textureScale, normalIntensity, panelSize]);

  const materialProps: MeshStandardMaterialParameters = {
    color: threeColor,
    roughness,
    metalness,
    normalMap,
    normalScale: new THREE.Vector2(1, 1),
    envMapIntensity: 0.9,
  };

  return <meshStandardMaterial {...materialProps} />;
};
